import { TimeScheduleCalendar } from '@spiaggeit/spit-datepicker'
import { Button } from '@spiaggeit/spit-ui'
import { ArrowLeft } from 'lucide-react'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { useSelectedDays } from '@/hooks/timeSchedule/useSelectedDays'
import { Service } from '@/models/service'
import { cartSlice } from '@/store/cartSlice'
import { activeElementQuotedSeatsAmountSelector } from '@/store/cartSlice/selectors/activeElementQuotedSeatsAmountSelector'
import { selectTotalGlobalSelectedQuantity } from '@/store/cartSlice/selectors/selectTotalGlobalSelectedQuantity'
import { serviceUnavailableDaysSelector } from '@/store/cartSlice/selectors/serviceUnavailableDaysSelector'
import { totalGlobalBoughtQuantitySelector } from '@/store/cartSlice/selectors/totalGlobalBoughtQuantitySelector'
import { cartChangeQuotableServiceQuantity } from '@/store/cartSlice/thunks/cartChangeQuotableServiceQuantity'
import {
  dayDifferenceSelector,
  insertPeriodSlice,
} from '@/store/insertPeriodSlice'
import { mapSelectedElementServices, mapSelectors } from '@/store/mapSlice'
import { timeScheduleSlice } from '@/store/timeScheduleSlice'
import { dialogTotalSelector } from '@/store/timeScheduleSlice/selectors/dialogTotalSelector'
import { serviceAmountSelector } from '@/store/timeScheduleSlice/selectors/serviceAmountSelector'
import { visibleTimeSchedulesSelector } from '@/store/timeScheduleSlice/selectors/visibleTimeSchedulesSelector'
import { SERVICE_LABEL_LIMIT } from '@/utils/constants'
import { convertLocalTimeToUTCTime } from '@/utils/dateUtils'
import { getGroupBoughtQuantity } from '@/utils/getGroupBoughtQuantity'
import {
  ItemQuantityBoundary,
  getItemQuantityLabel,
} from '@/utils/getItemQuantityLabel'
import { getServiceSingleLimit } from '@/utils/getServiceSingleLimit'
import { priceFormatter } from '@/utils/price'
import { sumServiceGroupSelectedQuantity } from '@/utils/sumServiceGroupSelectedQuantity'
import { getTimeScheduleKey } from '@/utils/timeSchedules'
import {
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../Sheet/Sheet'
import { TimeScheduleDialogMessage } from '../TimeScheduleDialog/TimeScheduleDialogMessage'
import { TimeScheduleQuantityPicker } from '../TimeScheduleDialog/TimeScheduleQuantityPicker'

interface Props {
  service: Service
}

type ServiceTimeSchedules = Parameters<
  typeof cartChangeQuotableServiceQuantity
>[0]['timeSchedules'][number]

export function SelectedServiceSheet(props: Props) {
  const dispatch = useAppDispatch()

  const { i18n, t } = useTranslation()

  const addedServices = useAppSelector(
    timeScheduleSlice.selectors.addedServices
  )
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const dayDifference = useAppSelector(dayDifferenceSelector)
  const period = useAppSelector(insertPeriodSlice.selectors.period)
  const selectedElementQuotedSeatsAmount = useAppSelector(
    activeElementQuotedSeatsAmountSelector
  )
  const selectedDays = useAppSelector(timeScheduleSlice.selectors.selectedDays)
  const serviceAmount = useAppSelector(serviceAmountSelector)
  const total = useAppSelector(dialogTotalSelector)
  const visibleTimeSchedules = useAppSelector(visibleTimeSchedulesSelector)
  const unavailableDays = useAppSelector((state) =>
    serviceUnavailableDaysSelector(state, props.service)
  )
  const selectedElementQuotableProducts = useAppSelector(
    cartSlice.selectors.selectedElementQuotableProducts
  )
  const selectedElementServices = useAppSelector(mapSelectedElementServices)
  const selectedElement = useAppSelector(mapSelectors.selectedElement)
  const totalGlobalBoughtQuantity = useAppSelector(
    totalGlobalBoughtQuantitySelector
  )
  const totalGlobalSelectedQuantity = useAppSelector(
    selectTotalGlobalSelectedQuantity
  )

  const ticketServices = selectedElementServices.ticketServices

  useSelectedDays()

  if (!selectedElement) {
    return null
  }

  // GLOBAL
  const globalAvailableQnt =
    (props.service.globalAvailableQnt ?? 0) - totalGlobalBoughtQuantity
  const globalReservedQnt =
    (props.service.globalReservedQnt ?? 0) + totalGlobalSelectedQuantity

  // GROUP
  const totalGroupBoughtQuantity = getGroupBoughtQuantity({
    groupId: props.service.serviceGroupId,
    selectedElementQuotableProducts,
    selectedService: {
      bought: serviceAmount,
      service: props.service,
    },
    ticketServices,
  })
  const totalGroupSelectedQuantity = sumServiceGroupSelectedQuantity({
    cartReservations,
    selectedElement,
    selectedElementQuotableProducts,
    service: props.service,
    serviceAmount,
    ticketServices,
  })

  const groupAvailableQnt =
    (props.service.groupAvailableQnt ?? 0) - totalGroupBoughtQuantity
  const groupReservedQnt =
    (props.service.groupReservedQnt ?? 0) + totalGroupSelectedQuantity

  const global =
    globalAvailableQnt !== undefined
      ? Math.min(
          globalAvailableQnt,
          (props.service.globalMaximumQnt ?? 0) - globalReservedQnt
        )
      : Infinity

  const group =
    groupAvailableQnt !== undefined
      ? Math.min(
          groupAvailableQnt ?? 0,
          (props.service.groupMaximumQnt ?? 0) - (groupReservedQnt ?? 0)
        )
      : Infinity

  const ticketLimit = getServiceSingleLimit({
    dayDifference,
    seatsAmount: selectedElementQuotedSeatsAmount,
    service: props.service,
    serviceInCartQuantity: serviceAmount,
  })

  const minMaxLabel = (() => {
    const labels: string[] = []

    const minLabel = getItemQuantityLabel({
      boundary: ItemQuantityBoundary.MIN,
      flatLimitAmount: props.service.minimumQuantity,
      limitType: props.service.minimumLimitType,
    })

    if (minLabel) {
      if (minLabel.trim()) {
        labels.push(minLabel)
      }
    } else if (props.service.minimumQuantity > 0) {
      labels.push(
        `${t('timeSchedulesDialog.min', { value: props.service.minimumQuantity })}`
      )
    }

    const maxLabel =
      ticketLimit < SERVICE_LABEL_LIMIT
        ? getItemQuantityLabel({
            boundary: ItemQuantityBoundary.MAX,
            flatLimitAmount: props.service.availableQuantity,
            limitType: props.service.maximumLimitType,
          })
        : ' '

    if (maxLabel) {
      if (maxLabel.trim()) {
        labels.push(maxLabel)
      }
    } else if (props.service.hasLimit && ticketLimit < SERVICE_LABEL_LIMIT) {
      labels.push(`${t('timeSchedulesDialog.max', { value: ticketLimit })}`)
    }

    const label = labels.join(' · ')

    return label ? ` · ${label}` : null
  })()

  function onClose() {
    dispatch(timeScheduleSlice.actions.reset())
  }

  function handleAdd(e: Event) {
    e.stopPropagation()
    e.preventDefault()

    dispatch(
      cartChangeQuotableServiceQuantity({
        days: selectedDays.map(convertLocalTimeToUTCTime),
        service: props.service,
        timeSchedules: Object.values(addedServices).reduce<
          ServiceTimeSchedules[]
        >((acc, service) => {
          const key = getTimeScheduleKey(service.timeSchedule)

          const foundTimeSchedule = visibleTimeSchedules.find(
            (ts) => getTimeScheduleKey(ts) === key
          )

          if (foundTimeSchedule) {
            acc.push({
              bought: service.bought,
              timeSchedule: foundTimeSchedule,
            })
          }

          return acc
        }, []),
      })
    )

    onClose()
  }

  if (period === null) {
    return null
  }

  return (
    <SheetContent
      className="flex flex-col justify-between gap-0 overflow-hidden lg:h-full"
      mobileSide="right"
      showCloseButton={false}
    >
      <div className="flex flex-grow flex-col justify-start max-lg:overflow-auto lg:overflow-hidden">
        <SheetTitle className="sr-only">
          <span>{props.service.name}</span>
        </SheetTitle>

        <SheetHeader className="relative sticky top-0 z-10 w-full border-b bg-white">
          <div className="flex items-center gap-2 p-4 font-bold">
            <ArrowLeft className="shrink-0 cursor-pointer" onClick={onClose} />
            <span className="grow text-center">{props.service.name}</span>
          </div>
        </SheetHeader>

        <div className="w-full p-4 lg:overflow-auto">
          {dayDifference > 1 ? (
            <div className="lg:py-2">
              <div className="font-bold">
                {t('timeSchedulesDialog.datepickerTitle')}
              </div>

              <TimeScheduleCalendar
                lang={i18n.language}
                maxDate={DateTime.fromISO(period.end).toJSDate()}
                minDate={DateTime.fromISO(period.start).toJSDate()}
                onDateSelect={(time) => {
                  dispatch(timeScheduleSlice.actions.daySelected(time))
                }}
                selectedDates={selectedDays}
                unavailableDays={unavailableDays}
              />
            </div>
          ) : null}

          <TimeScheduleDialogMessage
            selectedDays={selectedDays}
            timeSchedules={visibleTimeSchedules}
          />

          {visibleTimeSchedules.length > 0 ? (
            <div className="space-y-4">
              <div className="font-bold">
                {t('timeSchedulesDialog.timeSelect')}
                {minMaxLabel ? (
                  <span className="font-normal">{minMaxLabel}</span>
                ) : null}
              </div>

              {visibleTimeSchedules.map((timeSchedule, index) => (
                <TimeScheduleQuantityPicker
                  availability={{
                    global,
                    group,
                    service: ticketLimit,
                  }}
                  key={index}
                  service={props.service}
                  serviceAmount={serviceAmount}
                  timeSchedule={timeSchedule}
                />
              ))}
            </div>
          ) : null}
        </div>

        <SheetFooter className="sticky bottom-0 left-0 right-0 mt-auto flex items-center justify-between gap-2 border-t bg-white p-4">
          <div className="min-w-0">
            <div className="text-sm">{t('timeSchedulesDialog.total')}:</div>
            <div className="font-bold">{priceFormatter.format(total)}</div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              className="rounded"
              onClick={onClose}
              type="button"
              variant="outline"
            >
              {t('timeSchedulesDialog.cancel')}
            </Button>

            <Button
              className="rounded"
              disabled={
                selectedDays.length === 0 ||
                serviceAmount < props.service.minimumQuantity
              }
              onClick={handleAdd}
              type="button"
            >
              {t('timeSchedulesDialog.add')}
            </Button>
          </div>
        </SheetFooter>
      </div>
    </SheetContent>
  )
}
